import {
  parseErrors,
  type Kwargs,
  type Query,
  type ObjectMap,
  createRouter,
  type ConfigType,
  type Attachments,
} from 'tg-resources';
import { FetchResource as Resource } from '@tg-resources/fetch';
import { useSelector } from 'react-redux';

import isString from '../Utils/isString';
import type { RootState } from '../Redux';

const api = createRouter(
  {
    txp: createRouter(
      {
        // API(s) that do not need access token
        chatrooms: '/chatrooms',
        chatroom: '/chatroom/${chatId}',
        chatroomMedia: '/chatrooms/${chatId}/media',
        chatroomMembers: '/chatrooms/${chatId}/members',
        chatroomPinnedMessages: '/chatrooms/${chatId}/pinned-messages',
        chatroomMessages: '/chatrooms/${chatId}/messages/${limit}/${offset}',
        chatroomMessageStatuses: '/chatrooms/${chatId}/messages/${messageIdList}/statuses/all',
        updateChatroomMember: '/chatrooms/${chatId}/members/me',
        updateChatroomManager: '/chatrooms/${chatId}/manager/${userId}',
        domoToken: '/domo/embed/token',
        domoInsightsToken: '/domo/embed/insights/token',

        search: '/chatrooms/messages/search',

        getOrganizationMembers: '/organizations/${orgId}/members',
        getContacts: '/user/contacts',
        checkUserExists: '/user/exists',
        inviteNewUser: '/user/invite',
        inviteInfo: '/user/invite/${inviteFactor}',

        saveDeviceToken: '/user/devices/insert',

        createActivationCode: '/auth/application/activation/request',
        redirectToAdminPortal: '/auth/user/admin-portal/redirect',
        resetExpiredPassword: '/auth/reset-expired-password',
        passwordPolicy: '/auth/password-policy',

        createOffer: '/offer/donor',
        getOffers: '/offer/organization/donors',
        getOffer: '/offer/donor/${donorId}/organ/${organId}',
        getOrganizations: '/organization/all',
        getOrgDefaultCaseViewers: '/organization/${orgId}/case-access',
        cases: '/cases',
        createCaseView: '/cases/view',
        casesViews: '/cases/views',
        updateCaseView: '/cases/views/${casesViewId}',
        deleteCaseView: '/cases/views/${casesViewId}',
        tagFilters: '/cases/tags',
        getDonor: '/donor/${donorId}',
        updateDonorStatus: '/donor/${donorId}/status',
        createCase: '/case',
        updateDonor: '/donor/${donorId}',
        patchUpdateCase: '/cases/${caseId}',
        donorOptions: '/donors/options',
        donorFiles: '/donor/${donorId}/files',
        uploadDonorFile: '/donor/${donorId}/file/${fileName}',
        getPdfFields: '/donor/pdf_fields',
        importDonorData: '/cases/${caseId}/import-donor-data',
        tasks: '/donors/tasks/${workflow}',
        donorTask: '/donor/${donorId}/task/${taskId}',
        updateNote: '/donor/${donorId}/task/${taskId}/note',
        donorTasks: '/donor/${donorId}/tasks',
        updateStatus: '/donor/${donorId}/task/${taskId}/status',
        updateDonorTaskData: '/donor/${donorId}/task/${taskId}/data',
        updateDonorTaskDueDate: '/donor/${donorId}/task/${taskId}/due',
        donorFollowers: '/donor/${donorId}/followers',
        caseFollowersDetailed: '/case/${caseId}/followers_detailed',
        generateModularCasePdf: '/case/${caseId}/pdf',
        caseTimeline: '/case/${caseId}/timeline',
        caseInsightsEmbed: '/case/${caseId}/insights/embed',
        createCaseNote: '/case/${caseId}/notes',
        editCaseNote: '/case/${caseId}/notes/${noteId}',
        deleteCaseNote: '/case/${caseId}/notes/${noteId}',
        editCaseTask: '/case/${caseId}/task/${taskId}/edit',
        caseAdmin: '/case/${caseId}/admin/${userId}',

        follower: '/follower/${followerId}',
        createFollowerGroup: '/follower',
        updateFollowerName: '/follower/${followerId}/name',
        updateFollowerTasks: '/follower/${followerId}/tasks',
        updateFollowerUsers: '/follower/${followerId}/users',
        updateFollowerPermissions: '/follower/${followerId}/permission',
        updateFollowerRemoveUser: '/follower/${followerId}/users/${userId}',
        deleteFollowerInvite: '/follower/${followerId}/invite/${followerInviteId}',
        chatroomInvites: '/chatrooms/${chatId}/invites',
        deleteChatroomInvite: '/chatrooms/${chatId}/invites/${chatroomInviteId}',

        notifiableEventGroup: '/notifications/group/${notifiableGroupId}',

        featurePermissions: '/permissions/features',
        addPermission: '/permission/v2',
        updatePermission: '/permission/${permissionId}/v2',

        updateOrganizationAlert: '/services/organization-alert/${organizationAlertId}',
        createCaseServiceInfoRoom: '/services/providers/${providerId}/request',

        workflowOptions: '/references/workflow/options',
        workflowDefinition: '/reference/workflow/${key}/definition',
        domainData: '/references/data/${domain}',
        organizationType: '/organization/${organizationId}/type',

        teams: '/teams',
        team: '/team/${teamId}',
        createTeam: '/team/v2',
        updateTeam: '/team/${teamId}/v2',

        recipientOptions: '/recipients',
        recipientUploadRecords: '/recipients/records',

        caseNotificationPreview: '/case/notification-wizard/preview',
        caseNotificationSend: '/case/notification-wizard/send',

        users: '/users',

        analyticsDashboards: '/analytics/dashboards',

        providerServices: '/services/providers/${providerId}',
        subscribedServices: '/services/organization/${organizationId}',

        unosConnect: '/unos/oauth/connect',
        unosStatus: '/unos/oauth/status',
        unosDisconnect: '/unos/oauth/disconnect',
        unosDonor: '/unos/donors/${donorId}',
        unosUserOptions: '/unos/user/options',
        unosUserCenterCode: '/unos/user/center-code',
        unosUserCenterType: '/unos/user/center-type',
      },
      {
        useLodashTemplate: false,
        apiRoot: (import.meta.env.VITE_API_ROOT || '').replace(/\/$/, ''),
        parseErrors: (errorText: any, parentConfig: ConfigType) => {
          let error = null;

          if (isString(errorText)) {
            if (errorText) {
              try {
                error = JSON.parse(errorText);
              } catch (_e) {
                // if json parsing fails, handle as text
                error = errorText;
              }
            } else {
              error = '';
            }
          } else {
            error = errorText;
          }

          if (error && error.request_error) {
            const preparedError: Record<string, string[]> = {};

            (error.request_error || []).forEach((fieldError: RequestParametersError) => {
              preparedError[fieldError.parameter_name] = preparedError[fieldError.parameter_name] || [];
              (fieldError.violated_constraints || []).forEach((msg) => {
                preparedError[fieldError.parameter_name].push(msg);
              });
            });
            return parseErrors(preparedError, parentConfig);
          }
          if (error && error.error) {
            ({ error } = error);

            // Hackish solution to be able to handle recovery endpoint errors
            //  the errors for it are a json string with prepended statuscode
            if (isString(error) && (error.startsWith('403 - {') || error.startsWith('400 - {'))) {
              try {
                error = JSON.parse(error.substring(6));

                if (error && error.errorSummary) {
                  error = error.errorSummary;
                }
              } catch (_er) {
                // nothing to do
              }
            }

            error = {
              non_field_errors: error,
            };
          }

          return parseErrors(error, parentConfig);
        },
      },
      Resource,
    ),
  },
  {
    useLodashTemplate: false,
    headers: () => ({
      Accept: 'application/json',
      Apikey: import.meta.env.VITE_API_KEY || '',
    }),
    withCredentials: true,
  },
  Resource,
);

export type RequestData = ObjectMap<any> | string | null;
type RequestParametersError = {
  parameter_name: string;
  violated_constraints: string[];
};

export type ResourceParams =
  | {
      method: 'fetch';
      kwargs?: Kwargs | null;
      query?: Query;
    }
  | {
      method: 'post' | 'put' | 'del' | 'patch';
      kwargs?: Kwargs | null;
      data?: RequestData;
      query?: Query;
      attachments?: Attachments;
    };

export function useApiFetch() {
  const token = useSelector((state: RootState) => state.auth.accessToken);

  return buildResourceFetch(token);
}

export function buildResourceFetch(token?: string) {
  return (resource: Resource, params?: ResourceParams) => {
    const headers: HeadersInit = { Accept: 'application/json' };
    const paramsVal: ResourceParams = params ?? { method: 'fetch' };

    if (token && resource.config().withCredentials) {
      headers.Authorization = `Bearer ${token}`;
    }

    const requestConfig = { headers };
    const { kwargs, query } = paramsVal;

    if (paramsVal.method === 'fetch' || paramsVal.method === undefined) {
      return resource['fetch'](kwargs, query, requestConfig);
    } else {
      const { data = undefined, attachments = undefined } = paramsVal;
      return resource[paramsVal.method](kwargs, data, query, attachments, requestConfig);
    }
  };
}

export default api;
